/* kw-boost-oos.js - market-aware sold-out state for Boost AI product cards.
 *
 * Boost reports `available: true` for EVERY product: its flag is a roll-up across
 * all inventory locations, and the delivery profile has no DE->GB or GB->DE zone,
 * so stock held only at the other market's warehouse still renders as buyable.
 * Boost's API does carry the per-location breakdown in `inventory_quantities`,
 * but the storefront asks it for pre-rendered HTML, so that field never reaches
 * the page. This re-issues the query Boost just made with the template params
 * stripped, recomputes availability for the current market, badges the card and
 * sinks it to the end of the grid.
 *
 * Costs one extra request per Boost query, not per card. Full write-up, measured
 * numbers and verification: boost-oos/README.md in the Slumlord repo.
 */
(function () {
  'use strict';

  if (window.__kwBoostOOS) return;
  window.__kwBoostOOS = true;

  /* Shopify country -> the one inventory location that can ship to it. A country
     absent from this map disables the script rather than guessing. Adding a
     market means adding a line here AND opening the matching delivery zone. */
  var LOCATION_BY_COUNTRY = {
    DE: 'Online Warehouse',
    GB: 'UK Location'
  };

  var STRINGS = { de: 'Ausverkauft', en: 'Sold out' };

  /* Params that make Boost return rendered HTML instead of product JSON, plus
     the per-request noise that would defeat dedupe. */
  var STRIP = [
    'widgetId', 'templateId', 'simplifiedIntegration', 'build_filter_tree',
    'insert_banners', 'viewAs', 'productImageWidth', 'productPerRow', 'grid_col',
    'first_load', 'event_type', 't', '_', 'sid', 'customer_id'
  ];

  /* Printable on purpose. A unicode-escaped NUL separator decodes to a real
     control character on disk, which a browser code editor strips on paste.
     Product ids are numeric and option values are words, so '|' cannot collide. */
  var SEP = '|';

  var optionOk = Object.create(null);  // pid|optName|optValue -> bool (any variant)
  var productOk = Object.create(null); // productId            -> bool (any variant)
  var known = Object.create(null);     // productId            -> true

  function noop() {}

  function marketLocation() {
    var c = (window.Shopify && window.Shopify.country) || '';
    return LOCATION_BY_COUNTRY[String(c).toUpperCase()] || null;
  }

  function soldOutLabel() {
    var l = String((window.Shopify && window.Shopify.locale) || 'de').slice(0, 2);
    return STRINGS[l] || STRINGS.de;
  }

  var LOC = marketLocation();
  if (!LOC) return;

  /* Reproduces Shopify's market-scoped `available`. Control-tested against
     /products/<handle>.js on 60 live products: 60/60 exact. */
  function variantAvailableHere(v) {
    if (v.inventory_management !== 'shopify') return true; // untracked: always sellable
    if (v.inventory_policy === 'continue') return true;    // overselling allowed
    return ((v.inventory_quantities || {})[LOC] || 0) > 0;
  }

  function ingest(json) {
    var products = json && json.products;
    if (!products || !products.length) return false;

    for (var i = 0; i < products.length; i++) {
      var p = products[i];
      if (!p || !p.id || !p.variants) continue;

      var any = false;
      for (var j = 0; j < p.variants.length; j++) {
        var v = p.variants[j];
        var ok = variantAvailableHere(v);
        if (ok) any = true;

        /* An option value is buyable if ANY variant carrying it is buyable -
           products with a second option ("Schwarz / 20") have several per colour,
           so testing only the first match gets it wrong. */
        var mo = v.merged_options || [];
        for (var k = 0; k < mo.length; k++) {
          var m = String(mo[k]);
          var at = m.indexOf(':');
          if (at < 0) continue;
          var key = p.id + SEP + m.slice(0, at).toLowerCase() + SEP +
                    m.slice(at + 1).toLowerCase();
          if (!(key in optionOk)) optionOk[key] = false;
          if (ok) optionOk[key] = true;
        }
      }

      productOk[p.id] = any;
      known[p.id] = true;
    }
    return true;
  }

  /* ---- re-ask Boost's own query for JSON ---------------------------------- */

  var seen = Object.create(null);
  /* No product query yet = no Boost grid on this page (home, product pages,
     blog): the MutationObserver then returns at once instead of scanning. */
  var active = false;

  /* Match Boost's PRODUCT query on any of its endpoint paths. Do not match on the
     string "bc-sf-filter": Boost serves different integrations per theme, and the
     path changed from /bc-sf-filter/filter to /bc-sf-ssr-node/filter when the
     16 Sep theme was published, which silently disabled this whole script.
     Matching the last path segment survives that rename. `/search/redirects`,
     `/search/suggest` and `/ssr-template` correctly do not match. */
  function productQueryUrl(raw) {
    var u;
    try { u = new URL(raw); } catch (e) { return null; }
    if (u.hostname.indexOf('mybcapps') === -1) return null;
    if (!/\/(filter|search)$/.test(u.pathname)) return null;
    return u;
  }

  function query(raw) {
    var url = productQueryUrl(raw);
    if (!url) return;
    active = true;

    for (var s = 0; s < STRIP.length; s++) url.searchParams.delete(STRIP[s]);

    var key = url.toString();
    if (seen[key]) return;
    seen[key] = true;

    /* Reuse the request the head hook already started for this query, if any. */
    var early = window.__kwBoostP && window.__kwBoostP[key];
    (early || fetch(key).then(function (r) { return r.ok ? r.json() : null; }))
      .then(function (j) { if (j && ingest(j)) schedule(); })
      .catch(noop);
  }

  /* Fallback: queries that finished before this script ran, or that the head
     hook missed. Resource entries only appear once a response has ENDED. */
  function harvest() {
    var entries;
    try { entries = performance.getEntriesByType('resource'); } catch (e) { return; }
    for (var i = 0; i < entries.length; i++) query(entries[i].name);
  }

  /* Speed release B: snippets/boost-cookie-script.liquid wraps window.fetch in
     <head> and starts the JSON request (window.__kwBoostP, keyed by the
     stripped URL) the moment Boost starts its own, not after it. The data then
     usually lands before Boost paints the grid, and the sold-out cards are sunk
     before the first paint - no visible jump (it was CLS 0.19 on model pages). */
  for (var pk in (window.__kwBoostP || {})) query(pk);
  window.__kwBoostOnQ = query;

  /* ---- decorate ----------------------------------------------------------- */

  function addBadge(el) {
    var host = el.querySelector('.boost-sd__product-link-image') ||
               el.querySelector('.boost-sd__product-image-wrapper');
    if (!host || host.querySelector('.kw-boost-oos__badge')) return;
    var span = document.createElement('span');
    span.className = 'kw-boost-oos__badge';
    span.textContent = soldOutLabel();
    host.appendChild(span);
  }

  function removeBadge(el) {
    var b = el.querySelector('.kw-boost-oos__badge');
    if (b && b.parentNode) b.parentNode.removeChild(b);
  }

  function markSwatches(el, pid) {
    var inputs = el.querySelectorAll('.boost-sd__radio-input[data-swatch-type]');
    for (var i = 0; i < inputs.length; i++) {
      var inp = inputs[i];
      var key = pid + SEP +
        String(inp.getAttribute('data-swatch-type') || '').toLowerCase() + SEP +
        String(inp.getAttribute('value') || '').toLowerCase();
      if (!(key in optionOk)) continue;
      var opt = inp.closest('.boost-sd__product-swatch-option');
      if (opt) opt.classList.toggle('kw-boost-oos-swatch', optionOk[key] === false);
    }
  }

  function decorate(freshBatch) {
    var items = document.querySelectorAll('.boost-sd__product-item[data-product-id]');
    for (var i = 0; i < items.length; i++) {
      var el = items[i];
      var pid = el.getAttribute('data-product-id');
      if (!(pid in known)) continue;      // no data for this card: leave it alone

      var state = productOk[pid] === false ? '1' : '0';
      /* Skip cards already in the right state. Without this the badge we append
         re-triggers the MutationObserver every frame. */
      if (el.getAttribute('data-kw-oos') === state) continue;
      el.setAttribute('data-kw-oos', state);

      el.classList.toggle('kw-boost-oos', state === '1');
      if (state === '1') addBadge(el); else removeBadge(el);
      markSwatches(el, pid);

      /* Sinking a card the visitor can already see makes the grid jump. A card
         inserted in this same MutationObserver batch has not been painted yet,
         so it can move freely; a painted one only moves while it is wholly
         below the viewport, where nothing visible shifts. Once decided, the
         card stays put, so it never jumps later either. */
      if (state === '0') {
        el.classList.remove('kw-boost-oos--sink');
      } else if (!el.hasAttribute('data-kw-oos-sink')) {
        var sink = (freshBatch && firstSeen.get(el) === freshBatch) ||
                   el.getBoundingClientRect().top >= window.innerHeight;
        el.setAttribute('data-kw-oos-sink', sink ? '1' : '0');
        el.classList.toggle('kw-boost-oos--sink', sink);
      }
    }
  }

  /* MutationObserver callbacks run before the browser paints the mutation, so
     decorating here (when the data is already in) sinks sold-out cards before
     anyone sees them. `batch` numbers each callback; firstSeen records the
     callback in which each card first appeared. */
  var batch = 0;
  var firstSeen = new WeakMap();
  function onMutate() {
    if (!active) return;
    var cur = ++batch;
    var items = document.querySelectorAll('.boost-sd__product-item[data-product-id]');
    var fresh = false;
    for (var i = 0; i < items.length; i++) {
      if (!firstSeen.has(items[i])) { firstSeen.set(items[i], cur); fresh = true; }
    }
    if (fresh) decorate(cur); else schedule();
  }

  /* setTimeout, not requestAnimationFrame: rAF never fires while the tab is
     hidden, so a card restored from bfcache or decorated in a background tab
     would stay unmarked until the next repaint. */
  var pending = null;
  function schedule() {
    if (pending) return;
    pending = setTimeout(function () {
      pending = null;
      decorate(0);
    }, 30);
  }

  function start() {
    harvest();
    schedule();
    try {
      new PerformanceObserver(harvest).observe({ type: 'resource', buffered: true });
    } catch (e) {
      setInterval(harvest, 2000);
    }
    /* Cards already in the page have been painted: never "fresh". */
    var pre = document.querySelectorAll(".boost-sd__product-item[data-product-id]");
    for (var p = 0; p < pre.length; p++) firstSeen.set(pre[p], 0);
    new MutationObserver(onMutate).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
